import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import type { WeaponDefinition } from './types'

export interface ActiveProjectile {
  active: boolean
  definition: WeaponDefinition
  position: THREE.Vector3
  velocity: THREE.Vector3
  targetPoint: THREE.Vector3 | null
  life: number
  bouncesLeft: number
  mesh: THREE.Mesh
}

export type ProjectileHitCallback = (
  proj: ActiveProjectile,
  hitPoint: THREE.Vector3,
  hitCollider: RAPIER.Collider | null,
) => void

export class ProjectileManager {
  readonly pool: ActiveProjectile[] = []
  private readonly ray = {
    origin: { x: 0, y: 0, z: 0 },
    dir: { x: 0, y: 0, z: 0 },
  }
  private readonly tempStep = new THREE.Vector3()
  private readonly tempDir = new THREE.Vector3()
  private readonly tempTargetDir = new THREE.Vector3()
  private readonly hitPoint = new THREE.Vector3()

  constructor(scene: THREE.Scene, poolSize = 128) {
    const defaultGeom = new THREE.BoxGeometry(0.15, 0.15, 0.5)
    const defaultMat = new THREE.MeshBasicMaterial({ color: 0x00ffff })

    for (let i = 0; i < poolSize; i++) {
      const mesh = new THREE.Mesh(defaultGeom.clone(), defaultMat.clone())
      mesh.visible = false
      scene.add(mesh)

      this.pool.push({
        active: false,
        definition: null as any,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        targetPoint: null,
        life: 0,
        bouncesLeft: 0,
        mesh,
      })
    }
  }

  get capacity(): number {
    return this.pool.length
  }

  get activeCount(): number {
    let c = 0
    for (let i = 0; i < this.pool.length; i++) {
      if (this.pool[i].active) c++
    }
    return c
  }

  spawn(
    definition: WeaponDefinition,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    targetPoint: THREE.Vector3 | null = null,
  ): ActiveProjectile | null {
    const proj = this.pool.find((p) => !p.active)
    if (!proj) return null

    proj.active = true
    proj.definition = definition
    proj.position.copy(origin)
    proj.targetPoint = targetPoint ? targetPoint.clone() : null
    proj.life = 4.0 // 4 seconds max flight life
    proj.bouncesLeft = definition.bounces

    // Set velocity
    this.tempDir.copy(direction).normalize()
    if (definition.spreadAngle > 0) {
      // Add slight cone spread
      this.tempDir.x += (Math.random() - 0.5) * definition.spreadAngle
      this.tempDir.y += (Math.random() - 0.5) * definition.spreadAngle
      this.tempDir.z += (Math.random() - 0.5) * definition.spreadAngle
      this.tempDir.normalize()
    }

    proj.velocity.copy(this.tempDir).multiplyScalar(definition.projectileSpeed)

    // Update mesh visuals
    proj.mesh.scale.set(definition.size[0], definition.size[1], definition.size[2])
    const mat = proj.mesh.material as THREE.MeshBasicMaterial
    mat.color.setHex(definition.color)
    proj.mesh.position.copy(proj.position)
    this.orientMeshAlongVelocity(proj)
    proj.mesh.visible = true

    return proj
  }

  reflectProjectile(proj: ActiveProjectile, normal: THREE.Vector3) {
    if (proj.bouncesLeft <= 0) {
      this.deactivate(proj)
      return
    }

    proj.bouncesLeft -= 1
    const dot = proj.velocity.dot(normal)
    // V' = V - 2(V.N)N * restitution
    proj.velocity.sub(normal.clone().multiplyScalar(2 * dot)).multiplyScalar(0.75)

    this.tempDir.copy(proj.velocity).normalize()
    this.orientMeshAlongVelocity(proj)
  }

  deactivate(proj: ActiveProjectile) {
    proj.active = false
    proj.mesh.visible = false
  }

  /**
   * Align the projectile mesh so its local -Z faces the current travel
   * direction. Three sites previously inlined the same
   * `setFromUnitVectors((0,0,-1), this.tempDir)` block after a velocity
   * update (spawn, reflectProjectile, end-of-update). Caller is responsible
   * for populating this.tempDir with the normalized velocity first.
   */
  private orientMeshAlongVelocity(proj: ActiveProjectile): void {
    if (this.tempDir.lengthSq() > 0.0001) {
      proj.mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), this.tempDir)
    }
  }

  update(
    dt: number,
    world: RAPIER.World | null,
    onHit: ProjectileHitCallback | null = null,
    shooterBody?: RAPIER.RigidBody | null,
  ) {
    for (let i = 0; i < this.pool.length; i++) {
      const proj = this.pool[i]
      if (!proj.active) continue

      proj.life -= dt
      if (proj.life <= 0) {
        this.deactivate(proj)
        continue
      }

      // Homing missile steering
      if (proj.definition.homing && proj.targetPoint) {
        this.tempTargetDir.subVectors(proj.targetPoint, proj.position).normalize()
        const speed = proj.definition.projectileSpeed
        proj.velocity.lerp(this.tempTargetDir.multiplyScalar(speed), 4.0 * dt)
      }

      // Gravity drop
      if (proj.definition.gravity !== 0) {
        proj.velocity.y += proj.definition.gravity * dt
      }

      // Step translation
      this.tempStep.copy(proj.velocity).multiplyScalar(dt)
      const stepDist = this.tempStep.length()

      if (stepDist > 0.0001 && world) {
        this.tempDir.copy(this.tempStep).normalize()
        this.ray.origin.x = proj.position.x
        this.ray.origin.y = proj.position.y
        this.ray.origin.z = proj.position.z
        this.ray.dir.x = this.tempDir.x
        this.ray.dir.y = this.tempDir.y
        this.ray.dir.z = this.tempDir.z

        const hit = world.castRayAndGetNormal(
          this.ray as any,
          stepDist + 0.1,
          true,
          undefined,
          undefined,
          undefined,
          shooterBody ?? undefined,
        )

        if (hit) {
          this.hitPoint.copy(proj.position).addScaledVector(this.tempDir, hit.timeOfImpact)

          if (proj.bouncesLeft > 0) {
            // Reflect off surface
            const norm = hit.normal
              ? new THREE.Vector3(hit.normal.x, hit.normal.y, hit.normal.z)
              : new THREE.Vector3(0, 1, 0)
            this.reflectProjectile(proj, norm)
          } else {
            onHit?.(proj, this.hitPoint, hit.collider ?? null)
            if (!proj.definition.penetrating) {
              this.deactivate(proj)
              continue
            }
          }
        }
      }

      proj.position.add(this.tempStep)
      proj.mesh.position.copy(proj.position)

      this.tempDir.copy(proj.velocity).normalize()
      this.orientMeshAlongVelocity(proj)
    }
  }
}
